import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import { atom, useAtomValue } from "jotai";
import { useState } from "react";

import { Button } from "@whoosh/design-system/src/components/controls";
import { CodeInput } from "@whoosh/design-system/src/components/controls/CodeInput";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  VStack,
  Text,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { UpdateEmailOrPhoneProfileStepStateAtom } from "../../../atoms/stores";
import { useCreateEmailVerificationProfile } from "../../../hooks/useCreateEmailVerificationProfile";
import { useCreatePhoneVerificationProfile } from "../../../hooks/useCreatePhoneVerificationProfile";
import { useVerifyAndAssignEmailCodeProfile } from "../../../hooks/useVerifyAndAssignEmailCodeProfile";
import { useVerifyAndAssignPhoneCodeProfile } from "../../../hooks/useVerifyAndAssignPhoneCodeProfile";

const contactInfoRawValueAtom = atom((get) => {
  const state = get(UpdateEmailOrPhoneProfileStepStateAtom);
  if (state.step === "VerifyCode") {
    return state.email || state.phone;
  }
  return null;
});

const isPhoneOrEmailAtom = atom((get) => {
  const state = get(UpdateEmailOrPhoneProfileStepStateAtom);
  if (state.step === "VerifyCode") {
    if (state.email) {
      return "email";
    } else if (state.phone) {
      return "phone";
    }
  }
  return null;
});

const phoneUtil = PhoneNumberUtil.getInstance();
const contactInfoDisplayAtom = atom((get) => {
  const isPhoneOrEmail = get(isPhoneOrEmailAtom);
  const value = get(contactInfoRawValueAtom);
  if (isPhoneOrEmail === "phone" && value) {
    //If it's phone we must format the national number
    return phoneUtil.format(
      phoneUtil.parseAndKeepRawInput(value, "US"),
      PhoneNumberFormat.NATIONAL
    );
  } else {
    return value ?? "";
  }
});

export const VerifyCodeStep = ({
  updateEmailOrPhone,
}: {
  updateEmailOrPhone: "phone" | "email" | null;
}) => {
  const [didSubmitVerification, setDidSubmitVerification] = useState(false);
  const contactInfoRawValue = useAtomValue(contactInfoRawValueAtom);
  const { showToast } = useToast();
  const [createEmailVerification] = useCreateEmailVerificationProfile();
  const [createPhoneVerification] = useCreatePhoneVerificationProfile();
  const [verifyAndAssignEmailCode, isVerifyingAndAssigningEmail] =
    useVerifyAndAssignEmailCodeProfile();
  const [verifyAndAssignPhoneCode, isVerifyingAndAssigningPhone] =
    useVerifyAndAssignPhoneCodeProfile();
  const contactInfoDisplay = useAtomValue(contactInfoDisplayAtom);
  const handleSendResendCodeToast = () => {
    showToast({
      description: t`Verification code sent to: ${contactInfoDisplay}`,
      semanticStyle: "success",
      toastId: "resend-verification-code",
    });
  };
  const handleResendVerification = () => {
    if (!contactInfoRawValue) {
      return null;
    }
    if (updateEmailOrPhone === "email") {
      createEmailVerification(contactInfoRawValue, handleSendResendCodeToast);
    } else {
      createPhoneVerification(contactInfoRawValue, handleSendResendCodeToast);
    }
  };
  const handleError = () => {
    setCurrentCode(undefined);
    setHasError(true);
    setDidSubmitVerification(false);
  };

  const handleCodeSubmission = (code: string) => {
    if (
      updateEmailOrPhone === "email" &&
      contactInfoRawValue &&
      !isVerifyingAndAssigningEmail &&
      !didSubmitVerification
    ) {
      setDidSubmitVerification(true);
      verifyAndAssignEmailCode({
        email: contactInfoRawValue,
        code,
        onError: handleError,
      });
    } else if (
      updateEmailOrPhone === "phone" &&
      contactInfoRawValue &&
      !isVerifyingAndAssigningPhone &&
      !didSubmitVerification
    ) {
      setDidSubmitVerification(true);
      verifyAndAssignPhoneCode({
        phoneNumber: contactInfoRawValue,
        code,
        onError: handleError,
      });
    }
  };

  const [hasError, setHasError] = useState(false);
  const [currentCode, setCurrentCode] = useState<string | undefined>(undefined);
  return (
    <VStack paddingHorizontal={6} paddingBottom={6} flexGrow={1}>
      <VStack flexGrow={1}>
        <VStack mb={6}>
          <Text
            marginBottom={3}
            variant={"mainSmallSemiBold"}
            color="text-secondary"
          >
            {t`Verify your ` + " " + updateEmailOrPhone}
          </Text>
          <VStack gap={2}>
            <Text variant={"mainTitle2"}>{t`Enter the code sent to:`}</Text>
            <Text variant={"mainBaseSemiBold"}>{contactInfoDisplay}</Text>
          </VStack>
        </VStack>

        <CodeInput
          name={"verification input"}
          onChange={(code) => {
            setHasError(false);
            setCurrentCode(code);
          }}
          length={6}
          onSubmit={(code) => {
            handleCodeSubmission(code);
          }}
          value={currentCode}
          hasError={hasError}
        />
        <Box height={120} />
      </VStack>
      <VStack gap={6}>
        <Button
          variant={"ghost"}
          onPress={handleResendVerification}
          accessibilityLabel={t`Resend button`}
        >
          {t`Resend`}
        </Button>
      </VStack>
    </VStack>
  );
};

export const VerifyCodeStepFragmentString = graphql`
  fragment VerifyCodeStepProfileProfile_session on Session {
    currentClientProfile {
      member {
        club {
          name
        }
      }
    }
  }
`;
